import { extractApiErrorMessage, useUpdateTransportMutation } from "@ots-frontend/ots-data";
import { useState } from "react";
import { useOptionalOtsToast } from "../../components/OtsToast";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import { bulkTransportSaves, type BulkTransportChanges, type BulkTransportSide } from "../../tickets/bulk-edit/bulk-transport-edit";
import { BulkTransportEditDialog } from "../../tickets/bulk-edit/BulkTransportEditDialog";
import type { DriverActivityActionProvider } from "../driver-activity-actions";
import type { ActivityEntry } from "../driver-activity-view-model";

export type UseDriverActivityBulkEditToolsOptions = {
  regionId?: string;
  /** Whether this person holds a permission; everything is offered when omitted. */
  can?: (permission: string) => boolean;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

const hasTransport = (entry: ActivityEntry) => !!(entry.pickup ?? entry.delivery);

/** Edits the basics of many selected tickets at once: driver, equipment, and freight. Each save patches the loaded day. */
export const useDriverActivityBulkEditTools = ({ regionId, can = () => true, brand, colorScheme }: UseDriverActivityBulkEditToolsOptions = {}): DriverActivityActionProvider => {
  const [updateTransport] = useUpdateTransportMutation();
  const toast = useOptionalOtsToast();
  const [targets, setTargets] = useState<ActivityEntry[]>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const save = async (changes: BulkTransportChanges, side: BulkTransportSide) => {
    if (!targets) return;
    setSaving(true);
    setError(undefined);
    const results = await Promise.allSettled(bulkTransportSaves(targets, changes, side).map((transport) => updateTransport(transport).unwrap()));
    setSaving(false);
    const failed = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
    if (failed.length) {
      // What saved stays saved; the dialog stays open on what didn't.
      setError(`${failed.length} of ${results.length} could not be saved: ${extractApiErrorMessage(failed[0].reason) ?? "please try again."}`);
      return;
    }
    toast?.show({ title: `${results.length === 1 ? "1 transport" : `${results.length} transports`} updated`, tone: "success" });
    setTargets(undefined);
  };

  const offered = can("transport_edit");
  return {
    actions: offered ? [{
      id: "edit-selected",
      label: "Edit Selected Tickets",
      scope: "any",
      group: "tools",
      isEnabled: (entries) => entries.some(hasTransport),
    }] : [],
    handleAction: (actionId, entries) => {
      if (actionId !== "edit-selected" || !offered) return false;
      setError(undefined);
      setTargets(entries.filter(hasTransport));
      return true;
    },
    dialogs: (
      <BulkTransportEditDialog
        open={!!targets}
        count={targets?.length ?? 0}
        regionId={regionId}
        onSubmit={(changes, side) => void save(changes, side)}
        onClose={() => setTargets(undefined)}
        submitting={saving}
        error={error}
        brand={brand}
        colorScheme={colorScheme}/>
    ),
  };
};
